import type { ValidateFunction } from 'ajv';
import type {
  IConfigSource,
  GetValue,
  GetValueOption,
  GetValuesAll,
} from './loader.types';
import {
  LocalJsonSource,
  EnvironmentSource,
  GrowthBookSource,
  type GrowthBookSourceInitSettings,
  type GrowthBookSourceUpdateSettings,
} from './sources';

export type ConfigUpdateListener = () => void;

/**
 * Dynamically loads configuration values from a configuration source.
 */
export class ConfigLoader<T, I = unknown, U = unknown> {
  private readonly source: IConfigSource<T, I, U>;
  private readonly listeners = new Set<() => void>();

  /**
   * @param source - The config source.
   */
  constructor(source: IConfigSource<T, I, U>) {
    this.source = source;
  }

  /**
   * Async initialization if supported by source
   * @param settings - any params dictionary
   * @returns Promise<void>
   */
  async init(settings: I): Promise<void> {
    return this.source.init?.(settings);
  }

  /**
   * Async update if supported by source
   * @param settings - any params dictionary
   * @returns Promise<void>
   */
  async update(settings: U): Promise<void> {
    await this.source.update?.(settings);
    this.notify();
  }

  /**
   * Method to subscribe to changes
   */
  subscribe(listener: ConfigUpdateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners
   */
  private notify(): void {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        // Log the error so it can be debugged, but don't let it stop other listeners
        console.error('[ConfigLoader/notify] A config listener failed:', error);
      }
    });
  }

  /**
   * @param key - string key for the property to fetch.
   * @returns The NonNullable value.
   */
  get: GetValue<T> = (key) => {
    return this.source.getValue(key);
  };

  /**
   * @param key - string key for the property to fetch.
   * @returns A promise to the nullable value.
   */
  getOptional: GetValueOption<T> = (key) => {
    return this.source.getValueOptional(key);
  };

  /**
   *
   * @returns All values in the config source.
   */
  getAll: GetValuesAll<T> = () => {
    return this.source.getValuesAll();
  };
}

/**
 * Creates a new ConfigLoader for LocalJsonSource
 * @returns ConfigLoader<SchemaType>
 */
export const createLocalJsonConfigLoader = <SchemaType>(
  config: SchemaType,
  validateFn: ValidateFunction,
): ConfigLoader<SchemaType> =>
  new ConfigLoader<SchemaType>(
    new LocalJsonSource<SchemaType>(validateFn, config),
  );

/**
 * Creates a new ConfigLoader for EnvironmentSource (using process.env)
 * @returns ConfigLoader<SchemaType>
 */
export const createEnvConfigLoader = <SchemaType>(
  validateFn: ValidateFunction,
): ConfigLoader<SchemaType> =>
  new ConfigLoader<SchemaType>(
    new EnvironmentSource<SchemaType>(validateFn, process.env),
  );

/**
 * Creates a new ConfigLoader for GrowthBookSource
 * @returns ConfigLoader<SchemaType>
 */
export const createGrowthBookConfigLoader = <SchemaType>(
  validateFn: ValidateFunction,
): ConfigLoader<
  SchemaType,
  GrowthBookSourceInitSettings,
  GrowthBookSourceUpdateSettings
> =>
  new ConfigLoader<
    SchemaType,
    GrowthBookSourceInitSettings,
    GrowthBookSourceUpdateSettings
  >(new GrowthBookSource<SchemaType>(validateFn));
